import { Emitter } from '@recurly/recurly-js/lib/emitter';
import { GooglePayEvent } from '@recurly/recurly-js/lib/google-pay';
import { createLogger } from 'logging';

import { GooglePayError, GooglePayToken } from './GooglePayCheckout';
import { GooglePayInstance } from './GooglePayService';

type EventType = 'error' | 'token' | 'ready' | 'paymentAuthorized';

interface EventHandlers {
    error: (data: GooglePayError) => void;
    token: (data: GooglePayToken) => void;
    ready: (button?: HTMLButtonElement) => void;
    paymentAuthorized: () => void;
}

type MockOutcome = 'success' | 'error';
type Listener = (...args: unknown[]) => void;

export class MockGooglePayInstance implements GooglePayInstance {
    private eventHandlers: Map<EventType, EventHandlers[EventType]> = new Map();
    private mockDelay = 1000; // 1 second delay to simulate network
    private outcome: MockOutcome;
    private logger = createLogger('MockGooglePayInstance');

    constructor(outcome: MockOutcome = 'success') {
        this.outcome = outcome;
        // Simulate ready state after initialization
        setTimeout(() => {
            this.triggerReady();
        }, 100);
    }

    on(event: GooglePayEvent, handler: Listener): GooglePayInstance {
        this.eventHandlers.set(event, handler);
        return this;
    }

    start(): void {
        this.logger.info('Mock Google Pay: Starting payment flow...');

        // Simulate Google Pay opening and user interaction
        setTimeout(() => {
            this.simulateGooglePayFlow();
        }, this.mockDelay);
    }

    private triggerReady(): void {
        const readyHandler = this.eventHandlers.get('ready') as EventHandlers['ready'];
        if (readyHandler) {
            this.logger.info('Mock Google Pay: Ready state triggered');
            // Create a mock button element that can be clicked
            const mockButton = {
                click: () => {
                    this.logger.info('Mock Google Pay button clicked');
                    this.start();
                },
                children: [
                    {
                        click: () => {
                            this.logger.info('Mock Google Pay button child clicked');
                            this.start();
                        },
                    },
                ],
                innerHTML: '<div>Mock Google Pay Button</div>',
                onclick: () => {
                    this.logger.info('Mock Google Pay button onclick triggered');
                    this.start();
                },
                dispatchEvent: (event: Event) => {
                    this.logger.info({ eventType: event.type }, 'Mock Google Pay button event dispatched');
                    if (event.type === 'click') {
                        this.start();
                    }
                },
            } as unknown as HTMLButtonElement;
            readyHandler(mockButton);
        }
    }

    private simulateGooglePayFlow(): void {
        this.logger.info(`Mock Google Pay: Simulating ${this.outcome} outcome`);

        switch (this.outcome) {
            case 'success':
                this.triggerPaymentAuthorized();
                setTimeout(() => this.triggerSuccess(), 500);
                break;
            case 'error':
                this.triggerError();
                break;
            default:
                this.logger.warn(`Unknown outcome: ${this.outcome as string}, defaulting to success`);
                this.triggerPaymentAuthorized();
                setTimeout(() => this.triggerSuccess(), 500);
        }
    }

    private triggerPaymentAuthorized(): void {
        const paymentAuthorizedHandler = this.eventHandlers.get(
            'paymentAuthorized',
        ) as EventHandlers['paymentAuthorized'];
        if (paymentAuthorizedHandler) {
            this.logger.info('Mock Google Pay: Payment authorized');
            paymentAuthorizedHandler();
        }
    }

    private triggerSuccess(): void {
        const tokenHandler = this.eventHandlers.get('token') as EventHandlers['token'];
        if (tokenHandler) {
            // Generate token ID only when actually called (client-side only)
            const timestamp = typeof window !== 'undefined' ? Date.now() : 1234567890;
            const mockToken: GooglePayToken = {
                id: `mock_recurly_gpay_token_${timestamp}`,
                email: 'test.user@example.com',
            };

            this.logger.info({ mockToken }, 'Mock Google Pay: Success - Token generated');
            tokenHandler(mockToken);
        }
    }

    private triggerError(): void {
        const errorHandler = this.eventHandlers.get('error') as EventHandlers['error'];
        if (errorHandler) {
            const mockError: GooglePayError = {
                code: 'GOOGLE_PAY_MOCK_ERROR',
                message:
                    'There has been an error processing your payment. Retry your request or try a different payment method. Contact customer support at support@storyblocks.com for more help.',
                cause: 'Mock Google Pay error simulation',
            };

            this.logger.error({ mockError }, 'Mock Google Pay: Error triggered');
            errorHandler(mockError);
        }
    }

    once(event: GooglePayEvent, handler: Listener): GooglePayInstance {
        throw new Error('Method not implemented.');
    }

    off(event: GooglePayEvent, handler: Listener): GooglePayInstance {
        throw new Error('Method not implemented.');
    }

    emit(event: GooglePayEvent, ...args: unknown[]): GooglePayInstance {
        throw new Error('Method not implemented.');
    }

    listeners(event: GooglePayEvent): Listener[] {
        throw new Error('Method not implemented.');
    }

    hasListeners(event: GooglePayEvent): boolean {
        throw new Error('Method not implemented.');
    }
}

export function createMockGooglePayInstance(outcome: MockOutcome = 'success'): MockGooglePayInstance {
    return new MockGooglePayInstance(outcome);
}
